import { useCallback, useRef, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { GetNotificationScheduleResponse } from '@pushly/models/lib/structs/notifications/get-requests/get-notification-schedule-response'
import { NotificationScheduleService } from '../../services/notification-schedule'
import { NotificationService } from '../../services/notification'
import { AppState } from '../../stores/app'
import { StatusType } from '../../enums/status-type'
import { NotificationCardData } from '../../hooks/use-notifications'
import { notification } from 'antd'
import { useDomainEffect } from './use-domain-effect'

interface UseDraftsResult {
    data: NotificationCardData[]
    loading: boolean
    error: Error | null
    count: number
    hasMore: boolean
    refresh: () => void
    loadMore: () => void
    archiveDraft: (notificationId: number) => Promise<void>
}

export function useDrafts(options?: {
    enabled?: boolean
    sources?: string[]
    segmentIds?: Array<string | number>
}): UseDraftsResult {
    const enabled = options?.enabled !== false
    const sources = options?.sources
    const segmentIds = options?.segmentIds
    const scheduleService = useRef(Container.get(NotificationScheduleService)).current
    const notificationService = useRef(Container.get(NotificationService)).current
    const appState = useRef(Container.get(AppState)).current

    const [data, setData] = useState<NotificationCardData[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)

    const PAGE_SIZE = 10

    const fetchDrafts = useCallback(
        async (pageNum: number = 1, append: boolean = false) => {
            const domainId = appState.currentDomain?.id
            if (!domainId) return
            if (!enabled) return

            setLoading(true)
            setError(null)

            try {
                const res = await scheduleService.fetchAllByDomainId(domainId, {
                    query: {
                        limit: PAGE_SIZE,
                        page: pageNum,
                        status: StatusType.DRAFT.name,
                        source: sources?.length ? sources.join(',') : 'manual,trigger,feed',
                        includeSegments: true,
                        ...(segmentIds?.length ? { segment_ids: segmentIds.join(',') } : {}),
                    },
                    cancellationKey: 'mv.drafts.fetch',
                    showLoadingScreen: false,
                })

                if (res.ok) {
                    const rawSchedules: Record<string, unknown>[] = res.data ?? []
                    const mapped: NotificationCardData[] = rawSchedules.map((raw) => {
                        const s = GetNotificationScheduleResponse.parseJsonObject(raw)
                        return {
                            id: Number(s.id),
                            notificationId: s.notification_id,
                            title: s.template?.channels?.default?.title || 'Untitled',
                            body: s.template?.channels?.default?.body,
                            imageUrl: s.template?.channels?.default?.image_url,
                            status: StatusType.DRAFT.name,
                            source: s.source,
                            sendTime: s.delivery_spec?.send_date_utc ?? s.created_at,
                            lastEditedAt: s.updated_at ?? s.created_at,
                            createdAt: s.created_at,
                            channels: s.channels as string[],
                            audience: s.audience,
                            // created_by is returned by the API but not in the struct definition
                            createdBy: (raw as { created_by?: string }).created_by,
                        }
                    })

                    if (append) {
                        setData((prev) => [...prev, ...mapped])
                    } else {
                        setData(mapped)
                    }
                    setHasMore(mapped.length >= PAGE_SIZE)
                } else {
                    throw new Error('Failed to fetch drafts')
                }
            } catch (err) {
                setError(err instanceof Error ? err : new Error('Failed to fetch drafts'))
            } finally {
                setLoading(false)
            }
        },
        [appState, scheduleService, enabled, sources, segmentIds],
    )

    const refresh = useCallback(() => {
        setPage(1)
        fetchDrafts(1, false)
    }, [fetchDrafts])

    const loadMore = useCallback(() => {
        const nextPage = page + 1
        setPage(nextPage)
        fetchDrafts(nextPage, true)
    }, [page, fetchDrafts])

    const archiveDraft = useCallback(
        async (notificationId: number) => {
            const domainId = appState.currentDomain?.id
            if (!domainId) return

            try {
                await notificationService.archiveNotification(domainId, notificationId)
                setData((prev) => prev.filter((d) => Number(d.notificationId) !== notificationId))
            } catch {
                notification.error({
                    message: 'Failed to archive draft',
                    description: 'Please try again.',
                    duration: 5,
                })
            }
        },
        [appState, notificationService],
    )

    useDomainEffect(() => {
        setData([])
        setPage(1)
        fetchDrafts(1, false)
    }, [fetchDrafts])

    return { data, loading, error, count: data.length, hasMore, refresh, loadMore, archiveDraft }
}
